/*
 * © 2025 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NoUniqueTargetFoundError = exports.TargetNotFoundError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name TargetNotFoundError
 * @description Snyk was unable to resolve the imported target. Ensure that Snyk created the target and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-admin/snyk-projects#target](https://docs.snyk.io/snyk-admin/snyk-projects#target)
 * @summary Target not found
 * @category Target
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TargetNotFoundError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Target not found',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-target-0001',
            status: 404,
            description: `Snyk was unable to resolve the imported target. Ensure that Snyk created the target and try again.`,
            errorCode: 'SNYK-TARGET-0001',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-admin/snyk-projects#target',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TargetNotFoundError = TargetNotFoundError;
/**
 * @class
 * @name NoUniqueTargetFoundError
 * @description Snyk was unable to resolve a single target. Snyk found multiple targets configured for the same integration and repository URL pair. Ensure a unique target exists.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-admin/snyk-projects#target](https://docs.snyk.io/snyk-admin/snyk-projects#target)
 * @summary No unique target found
 * @category Target
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NoUniqueTargetFoundError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'No unique target found',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-target-0002',
            status: 422,
            description: `Snyk was unable to resolve a single target. Snyk found multiple targets configured for the same integration and repository URL pair. Ensure a unique target exists.`,
            errorCode: 'SNYK-TARGET-0002',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-admin/snyk-projects#target',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.NoUniqueTargetFoundError = NoUniqueTargetFoundError;
//# sourceMappingURL=Target-error-catalog.js.map